// @owners { team: patients }
import { Button, Column, LgSpacing } from '@alto/design-system';
import { useNavigation } from '@alto/navigation';
import { PriceOverrideReasonMap } from '@alto/scriptdash/alto/pricing/types/v3/price_override_reason';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Keyboard, TouchableWithoutFeedback } from 'react-native';
// eslint-disable-next-line import/no-deprecated
import { type BillingOverridePayload, autobillPrescription } from '~shared/actions/prescriptions';
import { useNextAvailableDatesForPrescriptions } from '~shared/features/next-available-date/queries/useNextAvailableDatesForPrescriptions';
import { type VacationSupplyFormValues, initialFormValues } from '~shared/features/vacation-supply/helpers';
import { formatDateStringForAPI } from '~shared/helpers/date';
// eslint-disable-next-line import/no-deprecated
import { fetchLatestVacationSupply } from '~shared/queries/useFetchLatestVacationSupply';
import { useDispatchShared } from '~shared/store';
import { useAddToCart } from '../../cart';
import { VacationSupplyFormFields } from './VacationSupplyFormFields';

type VacationOverrideFormProps = {
  readonly buttonLabel: string;
  readonly description?: string;
  readonly initialValues?: VacationSupplyFormValues;
  readonly prescriptionID: number;
  readonly onClose: () => void;
};

export const VacationOverrideForm = ({
  buttonLabel,
  description,
  initialValues = initialFormValues,
  prescriptionID,
  onClose,
}: VacationOverrideFormProps) => {
  'use no memo';
  const { navigate } = useNavigation();

  const dispatch = useDispatchShared();
  const { handleAddToCart } = useAddToCart();

  const { nextAvailableDate } = useNextAvailableDatesForPrescriptions({ prescriptionID });
  const earliestAvailableDate = nextAvailableDate?.earliest?.date || '';

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
    reset,
  } = useForm<VacationSupplyFormValues>({
    defaultValues: initialValues,
    mode: 'onTouched',
  });

  useEffect(() => {
    reset(initialValues);
  }, [initialValues, reset]);

  const handleSubmitVacationOverride = () => {
    handleSubmit(async (values: VacationSupplyFormValues) => {
      const vacationOverrideParams: BillingOverridePayload = {
        reason: PriceOverrideReasonMap.VACATION_SUPPLY,
        data: {
          travel_destination: values.travelDestination,
          travel_start_date: formatDateStringForAPI(values.startDate) || '',
          travel_end_date: formatDateStringForAPI(values.returnDate) || '',
        },
      };

      // eslint-disable-next-line import/no-deprecated
      const result = await dispatch(autobillPrescription(prescriptionID, vacationOverrideParams));

      if (result.autobillSuccess) {
        const addToCartResult = await handleAddToCart({
          resource_id: prescriptionID,
          resource_type: 'Prescription',
          resetOrderDate: true,
        });
        if (addToCartResult.success) {
          onClose();
        }
      } else {
        // eslint-disable-next-line import/no-deprecated
        const latestVacationSupplyResponse = await fetchLatestVacationSupply({
          prescription_id: prescriptionID,
          status: 'failed',
        });

        if (latestVacationSupplyResponse?.data) {
          navigate('RouteVacationRequestFailure', {
            prescriptionID,
            vacationSupply: latestVacationSupplyResponse.data,
          });
          onClose();
        }
      }
    })();
  };

  return (
    <TouchableWithoutFeedback onPress={Keyboard.dismiss}>
      <Column>
        <LgSpacing />
        <VacationSupplyFormFields
          control={control}
          description={description}
          earliestAvailableDate={earliestAvailableDate}
          initialValues={initialValues}
        />
        <LgSpacing />
        <Button
          label={buttonLabel}
          loading={isSubmitting}
          onPress={handleSubmitVacationOverride}
        />
      </Column>
    </TouchableWithoutFeedback>
  );
};
